import { NetworkStatus } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import MontuMarkdown from "@repo/components/src/shared/markdown";
import { Badge, BadgeIcon, BadgeText } from "@repo/components/src/ui/badge";
import { Box } from "@repo/components/src/ui/box";
import { Button, ButtonIcon, ButtonText } from "@repo/components/src/ui/button";
import { Divider } from "@repo/components/src/ui/divider";
import { Heading } from "@repo/components/src/ui/heading";
import { HStack } from "@repo/components/src/ui/hstack";
import { Icon } from "@repo/components/src/ui/icon";
import {
  Progress,
  ProgressFilledTrack,
} from "@repo/components/src/ui/progress";
import { Skeleton } from "@repo/components/src/ui/skeleton";
import { Text } from "@repo/components/src/ui/text";
import { VStack } from "@repo/components/src/ui/vstack";
import { formatMoneyFromFragment } from "@repo/components/src/utils/money";
import { WorkOrderForUserDocument } from "@repo/graphql/documents/workOrder/query.generated";
import {
  MontuWorkOrderPaymentMethodChoices,
  MontuWorkOrderStatusChoices,
} from "@repo/graphql/gql/graphql";
import dayjs from "dayjs";
import { ExternalPathString, Link } from "expo-router";
import {
  Calendar,
  CheckCircle,
  CircleDot,
  Clock,
  CreditCard,
  ExternalLink,
  FileText,
  Gift,
  Package,
  Send,
  ShieldCheck,
  Upload,
  XCircle,
} from "lucide-react-native";
import React, { ComponentProps } from "react";

type Props = {
  userId: string;
  brandId: string;
  campaignId: string;
};

const statusConfig: Partial<
  Record<
    MontuWorkOrderStatusChoices,
    {
      action: ComponentProps<typeof Badge>["action"];
      icon: typeof CheckCircle;
      progress: number;
      trackColor: string;
    }
  >
> = {
  [MontuWorkOrderStatusChoices.PendingPayment]: {
    action: "warning",
    icon: CreditCard,
    progress: 15,
    trackColor: "bg-warning-400",
  },
  [MontuWorkOrderStatusChoices.Pending]: {
    action: "warning",
    icon: Clock,
    progress: 30,
    trackColor: "bg-warning-400",
  },
  [MontuWorkOrderStatusChoices.Accepted]: {
    action: "info",
    icon: CheckCircle,
    progress: 60,
    trackColor: "bg-info-400",
  },
  [MontuWorkOrderStatusChoices.Finished]: {
    action: "success",
    icon: CheckCircle,
    progress: 100,
    trackColor: "bg-success-500",
  },
  [MontuWorkOrderStatusChoices.Declined]: {
    action: "error",
    icon: XCircle,
    progress: 100,
    trackColor: "bg-error-500",
  },
  [MontuWorkOrderStatusChoices.Cancelled]: {
    action: "error",
    icon: XCircle,
    progress: 100,
    trackColor: "bg-error-500",
  },
};

const paymentMethodLabel: Partial<
  Record<MontuWorkOrderPaymentMethodChoices, string>
> = {
  [MontuWorkOrderPaymentMethodChoices.Debit]: "Pago con tarjeta",
  [MontuWorkOrderPaymentMethodChoices.Wallet]: "Wallet Montu",
  [MontuWorkOrderPaymentMethodChoices.Credit]: "Factura",
};

const SharedWorkOrder = React.memo((props: Props) => {
  const { data, loading, networkStatus } = useQuery(WorkOrderForUserDocument, {
    variables: {
      campaignId: props.campaignId,
      userId: props.userId,
    },
    fetchPolicy: "cache-and-network",
  });

  const raw = data?.workOrder.getForUser;

  const workOrder = raw;

  const status = workOrder ? statusConfig[workOrder.status.enum] : undefined;

  const invoiceFile = raw?.invoiceFile;

  return (
    <Skeleton
      isLoaded={
        [
          NetworkStatus.ready,
          NetworkStatus.fetchMore,
          NetworkStatus.refetch,
        ].includes(networkStatus) || !!raw
      }
      className="min-h-96"
    >
      {!loading && !raw ? (
        <VStack className="items-center py-16" space="sm">
          <Icon as={FileText} className="text-typography-200" size="xl" />
          <Text action="muted">Sin orden de trabajo en esta campaña.</Text>
        </VStack>
      ) : (
        <VStack space="lg">
          {workOrder && status && (
            <VStack space="sm">
              <HStack className="items-center justify-between">
                <Badge action={status.action} size="lg" rounded="lg">
                  <BadgeIcon as={status.icon} />
                  <BadgeText bold>{workOrder.status.display}</BadgeText>
                </Badge>
                <HStack space="xs" className="items-center">
                  <Icon
                    as={Calendar}
                    size="2xs"
                    className="text-typography-400"
                  />
                  <Text size="xs" className="text-typography-500 capitalize">
                    {dayjs(workOrder.createdAt).localizedShortDate()}
                  </Text>
                </HStack>
              </HStack>
              <Progress value={status.progress} size="xs">
                <ProgressFilledTrack className={status.trackColor} />
              </Progress>
            </VStack>
          )}

          {workOrder &&
            workOrder.paymentMethod?.enum !==
              MontuWorkOrderPaymentMethodChoices.Free && (
              <>
                <Divider />

                <VStack space="xs">
                  <Heading size="sm">Resumen del pago</Heading>
                  <Box className="rounded-xl bg-background-50 p-4">
                    <VStack space="md">
                      {workOrder?.paymentMethod?.enum && (
                        <>
                          <HStack className="items-center justify-between">
                            <Text className="text-typography-600">
                              Método de pago
                            </Text>
                            <HStack space="sm" className="items-center">
                              <Icon
                                as={CreditCard}
                                size="xs"
                                className="text-typography-500"
                              />
                              <Text bold>
                                {paymentMethodLabel[
                                  workOrder.paymentMethod.enum
                                ] ?? workOrder.paymentMethod.display}
                              </Text>
                            </HStack>
                          </HStack>
                          <Divider />
                        </>
                      )}
                      <HStack className="items-center justify-between">
                        <Text className="text-typography-600">
                          Pago líquido
                        </Text>
                        <Text className="font-medium text-typography-900">
                          {formatMoneyFromFragment(workOrder?.cashPayment)}
                        </Text>
                      </HStack>
                      <HStack className="items-center justify-between">
                        <Text className="text-typography-600">Pago bruto</Text>
                        <Text className="font-medium text-typography-900">
                          {formatMoneyFromFragment(
                            workOrder?.paymentBeforeTaxes,
                          )}
                        </Text>
                      </HStack>
                      <Divider />
                      <HStack className="items-center justify-between">
                        <Text bold>Total pagado</Text>
                        <Heading size="md" action="brand">
                          {formatMoneyFromFragment(workOrder?.totalPayment)}
                        </Heading>
                      </HStack>
                      {invoiceFile && (
                        <>
                          <Divider />
                          <HStack className="items-center justify-between">
                            <Text className="text-typography-600">Factura</Text>
                            <Link
                              href={invoiceFile.url as ExternalPathString}
                              target="_blank"
                              asChild
                            >
                              <Button
                                variant="outline"
                                action="brand"
                                size="xs"
                              >
                                <ButtonIcon as={ExternalLink} />
                                <ButtonText>Ver factura</ButtonText>
                              </Button>
                            </Link>
                          </HStack>
                        </>
                      )}
                    </VStack>
                  </Box>
                </VStack>
              </>
            )}

          {workOrder?.hasGiftCard && (
            <VStack space="xs">
              <HStack space="xs" className="items-center">
                <Icon as={Gift} size="xs" className="text-warning-500" />
                <Heading size="sm">Gift Card</Heading>
              </HStack>
              <Box className="rounded-xl bg-background-50 p-4">
                <HStack className="items-center justify-between">
                  <Text className="text-typography-600">Monto</Text>
                  <Text className="font-medium text-typography-900">
                    {formatMoneyFromFragment(workOrder.giftCardAmount)}
                  </Text>
                </HStack>
                {workOrder.giftCardCondition && (
                  <>
                    <Divider className="my-3" />
                    <VStack space="xs">
                      <Text size="sm" className="text-typography-500">
                        Condición
                      </Text>
                      <Text size="sm">{workOrder.giftCardCondition}</Text>
                    </VStack>
                  </>
                )}
              </Box>
            </VStack>
          )}

          {raw && raw.workorderpriceproductSet.length > 0 && (
            <>
              <Divider />
              <VStack space="xs">
                <HStack space="xs" className="items-center">
                  <Icon as={Package} size="xs" className="text-info-500" />
                  <Heading size="sm">Canjes</Heading>
                </HStack>
                <VStack space="sm">
                  {raw.workorderpriceproductSet.map((ppRef) => {
                    const pp = ppRef;
                    const product = ppRef.product;
                    return (
                      <HStack
                        key={pp.id}
                        className="items-center justify-between rounded-lg bg-background-50 p-3"
                      >
                        <VStack>
                          <Text size="sm" bold>
                            {product.name}
                          </Text>
                          <Text size="xs" className="text-typography-500">
                            Cantidad: {pp.quantity}
                          </Text>
                        </VStack>
                        <Text className="font-medium text-typography-900">
                          {formatMoneyFromFragment(pp.totalAmount)}
                        </Text>
                      </HStack>
                    );
                  })}
                  {raw.priceProductTotalValue && (
                    <HStack className="items-center justify-between px-3">
                      <Text size="sm" bold>
                        Total canjes
                      </Text>
                      <Heading size="sm" action="brand">
                        {formatMoneyFromFragment(raw.priceProductTotalValue)}
                      </Heading>
                    </HStack>
                  )}
                </VStack>
              </VStack>
            </>
          )}

          {raw && raw.workorderdetailSet.length > 0 && (
            <>
              <Divider />
              <VStack space="sm">
                <HStack className="items-center justify-between">
                  <Heading size="sm">Contenidos</Heading>
                  <Badge
                    action="muted"
                    size="sm"
                    rounded="full"
                    variant="outline"
                  >
                    <BadgeText>{raw.workorderdetailSet.length}</BadgeText>
                  </Badge>
                </HStack>
                {raw.workorderdetailSet.map((detailRef) => {
                  const detail = detailRef;
                  const postType = detailRef.postType;
                  const approvals = detailRef.contentapprovalSet;
                  const latestApproval = Array.isArray(approvals)
                    ? approvals[0]
                    : undefined;

                  return (
                    <Box
                      key={detail.id}
                      className="rounded-xl bg-background-50 p-4"
                    >
                      <HStack className="items-center justify-between mb-3">
                        <Text bold>
                          {postType?.fullName ?? postType?.name ?? "Contenido"}
                        </Text>
                        {latestApproval && (
                          <Badge
                            action="muted"
                            size="sm"
                            rounded="lg"
                            variant="outline"
                          >
                            <BadgeIcon as={CircleDot} />
                            <BadgeText>
                              {latestApproval.status.display}
                            </BadgeText>
                          </Badge>
                        )}
                      </HStack>
                      <HStack space="lg" className="flex-wrap">
                        <HStack space="xs" className="items-center">
                          <Icon
                            as={Calendar}
                            size="2xs"
                            className="text-typography-400"
                          />
                          <Text size="xs" className="text-typography-500">
                            Límite:{" "}
                            <Text size="xs" className="capitalize">
                              {dayjs(detail.date).localizedShortDate()}
                            </Text>
                          </Text>
                        </HStack>
                        {detail.sendContentDate && (
                          <HStack space="xs" className="items-center">
                            <Icon
                              as={Send}
                              size="2xs"
                              className="text-typography-400"
                            />
                            <Text size="xs" className="text-typography-500">
                              Envío:{" "}
                              <Text size="xs" className="capitalize">
                                {dayjs(
                                  detail.sendContentDate,
                                ).localizedShortDate()}
                              </Text>
                            </Text>
                          </HStack>
                        )}
                        <HStack space="xs" className="items-center">
                          <Icon
                            as={Upload}
                            size="2xs"
                            className="text-typography-400"
                          />
                          <Text size="xs" className="text-typography-500">
                            Sube: {detail.uploader.display}
                          </Text>
                        </HStack>
                      </HStack>
                      {(detail.hasReview || detail.rightOfUse) && (
                        <HStack space="lg" className="mt-2">
                          {detail.hasReview && (
                            <Badge
                              action="info"
                              size="sm"
                              rounded="lg"
                              variant="outline"
                            >
                              <BadgeText>Requiere revisión</BadgeText>
                            </Badge>
                          )}
                          {detail.rightOfUse && (
                            <HStack space="xs" className="items-center">
                              <Icon
                                as={ShieldCheck}
                                size="2xs"
                                className="text-typography-400"
                              />
                              <Text size="xs" className="text-typography-500">
                                {detail.rightOfUse.display}
                                {detail.rightOfUseMonths
                                  ? ` · ${detail.rightOfUseMonths} meses`
                                  : ""}
                              </Text>
                            </HStack>
                          )}
                        </HStack>
                      )}
                      {detail.description && (
                        <Box className="mt-3 rounded-lg bg-background-0 border border-outline-100 p-3">
                          <MontuMarkdown baseSize={12}>
                            {detail.description}
                          </MontuMarkdown>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </VStack>
            </>
          )}

          {raw && raw.additionalConditions.length > 0 && (
            <>
              <Divider />
              <VStack space="xs">
                <Heading size="sm">Condiciones adicionales</Heading>
                <VStack space="sm">
                  {raw.additionalConditions.map((condRef) => {
                    const condition = condRef;
                    return (
                      <HStack
                        key={condition.id}
                        className="rounded-lg bg-background-50 p-3"
                        space="sm"
                      >
                        <Text className="text-typography-400">•</Text>
                        <Text size="sm" className="flex-1">
                          {condition.condition}
                        </Text>
                      </HStack>
                    );
                  })}
                </VStack>
              </VStack>
            </>
          )}

          {raw && raw.preUploadRequirements.length > 0 && (
            <>
              <Divider />
              <VStack space="xs">
                <Heading size="sm">Requisitos previos</Heading>
                <VStack space="sm">
                  {raw.preUploadRequirements.map((reqRef) => {
                    const req = reqRef;
                    return (
                      <HStack
                        key={req.id}
                        className="rounded-lg bg-background-50 p-3"
                        space="sm"
                      >
                        <Text className="text-typography-400">•</Text>
                        <Text size="sm" className="flex-1">
                          {req.requirement.display}
                        </Text>
                      </HStack>
                    );
                  })}
                </VStack>
              </VStack>
            </>
          )}
        </VStack>
      )}
    </Skeleton>
  );
});

SharedWorkOrder.displayName = "SharedWorkOrder";

export default SharedWorkOrder;
